import React from "react";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { isActive } from "./paymentUtils";

/** Um meio de pagamento: ícone, nome, status, chave, interruptor e ações */
function PaymentMethodCard({ payment, editing, toggling, deleting, onEdit, onToggleActive, onDelete }) {
  const active = isActive(payment);
  const name = payment.name || "Sem nome";
  const busy = toggling || deleting;

  return (
    <article
      className={cn(
        "relative rounded-2xl border bg-card p-3.5 shadow-soft transition-all",
        editing
          ? "border-2 border-primary/60 shadow-md"
          : "border-border hover:border-brand-pistachio/60",
        !active && !editing && "bg-surface-low"
      )}
    >
      {editing && (
        <span className="absolute -top-2.5 right-4 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground shadow-sm">
          Em edição
        </span>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={cn(
              "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-lg",
              active ? "bg-surface-container" : "bg-muted grayscale",
              !payment.icon && "font-display text-sm font-bold text-primary"
            )}
            aria-hidden="true"
          >
            {payment.icon || name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h3
              className={cn(
                "truncate text-sm font-bold leading-tight",
                active ? "text-foreground" : "text-muted-foreground"
              )}
              title={name}
            >
              {name}
            </h3>
            <div className="mt-0.5 flex min-w-0 items-center gap-1.5">
              <span
                className={cn(
                  "inline-flex flex-shrink-0 items-center rounded border px-1.5 text-[10px] font-medium",
                  active
                    ? "border-brand-pistachio/50 bg-secondary text-secondary-foreground"
                    : "border-terra/30 bg-terra-soft text-terra"
                )}
              >
                {active ? "Ativo" : "Inativo"}
              </span>
              <span className="truncate font-mono text-[11px] text-muted-foreground" title={payment.id}>
                {payment.id}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1.5 pt-1">
          {toggling && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          <Switch
            checked={active}
            onCheckedChange={(checked) => onToggleActive(payment, checked)}
            disabled={busy}
            aria-label={`${active ? "Desativar" : "Ativar"} ${name}`}
          />
        </div>
      </div>

      <div className="mt-3 flex items-center justify-end gap-2 border-t border-border/60 pt-2.5">
        <button
          type="button"
          onClick={() => onEdit(payment)}
          disabled={busy}
          className={cn(
            "flex h-8 items-center gap-1 rounded-full border px-3 text-xs transition-colors disabled:opacity-60",
            editing
              ? "border-brand-pistachio/60 bg-secondary font-semibold text-primary"
              : "border-border bg-surface-container font-medium text-foreground hover:bg-surface-high"
          )}
        >
          <Pencil className="h-3.5 w-3.5" />
          {editing ? "Editando..." : "Editar"}
        </button>
        <button
          type="button"
          onClick={() => onDelete(payment)}
          disabled={busy}
          aria-label={`Excluir ${name}`}
          className="flex h-8 items-center gap-1 rounded-full border border-destructive/25 bg-destructive/5 px-3 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-60"
        >
          {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          Excluir
        </button>
      </div>
    </article>
  );
}

export default React.memo(PaymentMethodCard);
